"use client";

import { Mail, Phone, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
export default function YourRightsAndContactInformation() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: section1Ref,
    isVisible: section1Visible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: section2Ref,
    isVisible: section2Visible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: section3Ref,
    isVisible: section3Visible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: section4Ref,
    isVisible: section4Visible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: section5Ref,
    isVisible: section5Visible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: section6Ref,
    isVisible: section6Visible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: section7Ref,
    isVisible: section7Visible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: section8Ref,
    isVisible: section8Visible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: section9Ref,
    isVisible: section9Visible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: section10Ref,
    isVisible: section10Visible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contactRef,
    isVisible: contactVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section className="bg-background py-16 md:py-24">
      <div className="container max-w-5xl">
        {}
        <div ref={headerRef} className={`text-center mb-12 transition-all duration-[var(--duration-slow)] ease-[var(--ease-out-back)] ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4">
            IronPeak Men's Health — Privacy Policy
          </h1>
          <p className="text-lg text-muted-dark">
            Effective Date: <span className="font-semibold">11/1/2025</span>
          </p>
          <p className="text-base text-muted-dark mt-4 leading-relaxed max-w-3xl mx-auto">
            IronPeak Men's Health ("we," "our," "the Clinic") is committed to protecting your privacy. 
            This Privacy Policy explains what information we collect, how we use it, how we protect it, 
            and what rights you have when using our website, services, and telemedicine platform.
          </p>
          <p className="text-sm text-muted-dark mt-2">
            This policy applies to <span className="font-medium">ironpeakmenshealth.com</span>, 
            any associated subdomains, and all digital services offered by IronPeak Men's Health.
          </p>
        </div>

        {}
        <div ref={section1Ref} className={`mb-12 transition-all duration-[var(--duration-slow)] ease-[var(--ease-out-back)] ${section1Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Card className="bg-white border-border shadow-soft">
            <CardHeader>
              <CardTitle className="text-2xl md:text-3xl font-bold text-foreground">
                1. Information We Collect
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-muted-dark leading-relaxed">
                We may collect the following categories of information:
              </p>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    A. Personal Information You Provide
                  </h3>
                  <p className="text-muted-dark leading-relaxed mb-3">
                    When you fill out forms, schedule appointments, submit your contact information, 
                    or purchase services, we may collect:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-dark ml-4">
                    <li>Name</li>
                    <li>Email address</li>
                    <li>Phone number</li>
                    <li>Mailing address</li>
                    <li>Payment information (processed securely through third-party vendors)</li>
                    <li>Information you voluntarily submit through contact forms or intake forms</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    B. Protected Health Information (PHI)
                  </h3>
                  <p className="text-muted-dark leading-relaxed mb-3">
                    When you become a patient or submit medical information, the information is protected 
                    under HIPAA. Examples include:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-dark ml-4">
                    <li>Medical history</li>
                    <li>Lab results</li>
                    <li>Treatment plans</li>
                    <li>Telemedicine interactions</li>
                    <li>Prescription information</li>
                  </ul>
                  <p className="text-muted-dark leading-relaxed mt-3 font-medium">
                    PHI is stored only within HIPAA-compliant systems.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    C. Automatically Collected Information
                  </h3>
                  <p className="text-muted-dark leading-relaxed mb-3">
                    We may automatically collect:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-dark ml-4">
                    <li>IP address</li>
                    <li>Browser type</li>
                    <li>Device information</li>
                    <li>Pages viewed</li>
                    <li>Time spent on the site</li>
                    <li>Referring URLs</li>
                  </ul>
                  <p className="text-muted-dark leading-relaxed mt-3">
                    This data is collected through standard analytics tools such as Google Analytics or similar services.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    D. Cookies & Tracking Technologies
                  </h3>
                  <p className="text-muted-dark leading-relaxed mb-3">
                    We may use cookies, pixels, and similar technologies to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-dark ml-4">
                    <li>Improve website user experience</li>
                    <li>Track analytics</li>
                    <li>Personalize content</li>
                    <li>Support remarketing or advertising campaigns</li>
                  </ul>
                  <p className="text-muted-dark leading-relaxed mt-3">
                    You may disable cookies through your browser settings.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {}
        <div ref={section2Ref} className={`mb-12 transition-all duration-[var(--duration-slow)] ease-[var(--ease-out-back)] ${section2Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Card className="bg-white border-border shadow-soft">
            <CardHeader>
              <CardTitle className="text-2xl md:text-3xl font-bold text-foreground">
                2. How We Use Your Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-dark leading-relaxed">
                We use your information to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-dark ml-4">
                <li>Provide telemedicine services</li>
                <li>Communicate with you about appointments, inquiries, or services</li>
                <li>Deliver lab results, treatment plans, and follow-up instructions</li>
                <li>Administer our website and improve functionality</li>
                <li>Send promotional or educational messages (only with your consent)</li>
                <li>Maintain compliance with legal and regulatory requirements</li>
              </ul>
              <p className="text-muted-dark leading-relaxed font-semibold mt-4">
                We do not sell your personal information, PHI, or browsing data.
              </p>
            </CardContent>
          </Card>
        </div>

        {}
        <div ref={section3Ref} className={`mb-12 transition-all duration-[var(--duration-slow)] ease-[var(--ease-out-back)] ${section3Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Card className="bg-white border-border shadow-soft">
            <CardHeader>
              <CardTitle className="text-2xl md:text-3xl font-bold text-foreground">
                3. How We Protect Your Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-dark leading-relaxed">
                We use administrative, technical, and physical safeguards to protect your personal 
                information and PHI, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-dark ml-4">
                <li>HIPAA-compliant telemedicine platforms</li>
                <li>Encrypted data transmission</li>
                <li>Secure servers and firewalls</li>
                <li>Access controls and authentication</li>
                <li>Vendor agreements (BAAs) with third-party medical service providers</li>
              </ul>
              <p className="text-muted-dark leading-relaxed mt-4">
                However, no method of transmission over the internet is 100% secure. We strive to use 
                best-practice safeguards but cannot guarantee absolute security.
              </p>
            </CardContent>
          </Card>
        </div>

        {}
        <div ref={section4Ref} className={`mb-12 transition-all duration-[var(--duration-slow)] ease-[var(--ease-out-back)] ${section4Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Card className="bg-white border-border shadow-soft">
            <CardHeader>
              <CardTitle className="text-2xl md:text-3xl font-bold text-foreground">
                4. Sharing of Your Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-muted-dark leading-relaxed">
                We may share your information only in the following circumstances:
              </p>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    A. With HIPAA-Compliant Partners
                  </h3>
                  <p className="text-muted-dark leading-relaxed mb-3">Such as:</p>
                  <ul className="list-disc list-inside space-y-2 text-muted-dark ml-4">
                    <li>Labs (e.g., Quest, LabCorp)</li>
                    <li>Pharmacies</li>
                    <li>Telemedicine platforms</li>
                    <li>Payment processors</li>
                    <li>Medical record systems</li>
                  </ul>
                  <p className="text-muted-dark leading-relaxed mt-3 font-medium">
                    All medical partners are required to maintain HIPAA compliance.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    B. Legal Requirements
                  </h3>
                  <p className="text-muted-dark leading-relaxed mb-3">We may disclose information:</p>
                  <ul className="list-disc list-inside space-y-2 text-muted-dark ml-4">
                    <li>To comply with state or federal law</li>
                    <li>In response to subpoenas or court orders</li>
                    <li>To prevent fraud, security threats, or harm</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    C. Third-Party Service Providers
                  </h3>
                  <p className="text-muted-dark leading-relaxed mb-3">For non-medical purposes:</p>
                  <ul className="list-disc list-inside space-y-2 text-muted-dark ml-4">
                    <li>Website hosting</li>
                    <li>Marketing tools</li>
                    <li>Analytics providers</li>
                  </ul>
                  <p className="text-muted-dark leading-relaxed mt-3">
                    These partners are restricted from using your information outside of supporting our services.
                  </p>

                  <div className="mt-6 pt-6 border-t border-border">
                    <h4 className="text-lg font-semibold text-foreground mb-3">Data Sharing</h4>
                    <ul className="list-disc list-inside space-y-2 text-muted-dark ml-4">
                      <li>Customer data is not shared with 3rd parties for promotional or marketing purposes.</li>
                      <li>Mobile opt-in and consent are never shared with anyone for any purpose. Any information sharing that may be mentioned elsewhere in this policy excludes mobile opt-in data.</li>
                    </ul>
                  </div>

                  <div className="mt-6 pt-6 border-t border-border">
                    <h4 className="text-lg font-semibold text-foreground mb-3">Messaging Terms and Conditions</h4>
                    <p className="text-muted-dark leading-relaxed mb-3">
                      By providing your phone number and agreeing to receive texts, you consent to receive text messages from IronPeak Men's Health - Michael Flores DO, from 469557-5547 regarding account notification, customer care. Consent is not a condition of purchase. Message frequency varies. Message & data rates may apply. You can reply STOP to unsubscribe at any time or HELP for assistance. You can also contact us at 469557-5547 or Hello@ironpeakmenshealth.com. Mobile opt-in information is never shared with third parties.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {}
        <div ref={section5Ref} className={`mb-12 transition-all duration-[var(--duration-slow)] ease-[var(--ease-out-back)] ${section5Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Card className="bg-white border-border shadow-soft">
            <CardHeader>
              <CardTitle className="text-2xl md:text-3xl font-bold text-foreground">
                5. Your Rights
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-muted-dark leading-relaxed">
                Depending on whether you are a website visitor or patient, you may exercise certain rights:
              </p>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    HIPAA Patient Rights
                  </h3>
                  <p className="text-muted-dark leading-relaxed mb-3">You may:</p>
                  <ul className="list-disc list-inside space-y-2 text-muted-dark ml-4">
                    <li>Request access to your medical records</li>
                    <li>Request corrections to your records</li>
                    <li>Request an accounting of disclosures</li>
                    <li>Request communication preferences</li>
                    <li>Request restrictions on PHI use (as allowed by law)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    Website Visitor Rights
                  </h3>
                  <p className="text-muted-dark leading-relaxed mb-3">You may:</p>
                  <ul className="list-disc list-inside space-y-2 text-muted-dark ml-4">
                    <li>Opt out of marketing emails or SMS messages</li>
                    <li>Request deletion of non-medical information</li>
                    <li>Manage cookie preferences via your browser</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {}
        <div ref={section6Ref} className={`mb-12 transition-all duration-[var(--duration-slow)] ease-[var(--ease-out-back)] ${section6Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Card className="bg-white border-border shadow-soft">
            <CardHeader>
              <CardTitle className="text-2xl md:text-3xl font-bold text-foreground">
                6. Communications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-dark leading-relaxed">
                By providing a phone number or email, you consent to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-dark ml-4">
                <li>Appointment reminders</li>
                <li>Service-related messages</li>
                <li>SMS messages or email marketing (optional; you can opt out anytime)</li>
              </ul>
              <p className="text-muted-dark leading-relaxed font-medium mt-4">
                Text message rates may apply.
              </p>
            </CardContent>
          </Card>
        </div>

        {}
        <div ref={section7Ref} className={`mb-12 transition-all duration-[var(--duration-slow)] ease-[var(--ease-out-back)] ${section7Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Card className="bg-white border-border shadow-soft">
            <CardHeader>
              <CardTitle className="text-2xl md:text-3xl font-bold text-foreground">
                7. Children's Privacy
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-dark leading-relaxed">
                IronPeak Men's Health does not provide services to or knowingly collect information 
                from individuals under 18 years of age.
              </p>
            </CardContent>
          </Card>
        </div>

        {}
        <div ref={section8Ref} className={`mb-12 transition-all duration-[var(--duration-slow)] ease-[var(--ease-out-back)] ${section8Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Card className="bg-white border-border shadow-soft">
            <CardHeader>
              <CardTitle className="text-2xl md:text-3xl font-bold text-foreground">
                8. Third-Party Links
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-dark leading-relaxed">
                Our website may contain links to external websites. We are not responsible for the 
                privacy practices or content of those websites.
              </p>
            </CardContent>
          </Card>
        </div>

        {}
        <div ref={section9Ref} className={`mb-12 transition-all duration-[var(--duration-slow)] ease-[var(--ease-out-back)] ${section9Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Card className="bg-white border-border shadow-soft">
            <CardHeader>
              <CardTitle className="text-2xl md:text-3xl font-bold text-foreground">
                9. Changes to This Privacy Policy
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-dark leading-relaxed">
                We may update this Privacy Policy periodically. Updates will be posted on this page 
                with a new "Effective Date." Continued use of the website constitutes acceptance of 
                the updated policy.
              </p>
            </CardContent>
          </Card>
        </div>

        {}
        <div ref={section10Ref} className={`mb-12 transition-all duration-[var(--duration-slow)] ease-[var(--ease-out-back)] ${section10Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Card className="bg-primary-light-bg border-primary/20 shadow-medium">
            <CardHeader>
              <CardTitle className="text-2xl md:text-3xl font-bold text-foreground">
                10. Contact Us
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-muted-dark leading-relaxed">
                For questions about this Privacy Policy or your information, contact:
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground text-lg mb-1">IronPeak Men's Health</p>
                    <p className="text-muted-dark">Prosper, Texas</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                  <div>
                    <p className="text-muted-dark">
                      <Link to="tel:+14695575547" className="text-primary hover:text-primary-hover transition-colors font-medium">
                        (469) 557-5547
                      </Link>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                  <div>
                    <p className="text-muted-dark">
                      <Link to="https://ironpeakmenshealth.com" newTab className="text-primary hover:text-primary-hover transition-colors font-medium">
                        ironpeakmenshealth.com
                      </Link>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                  <div>
                    <p className="text-muted-dark">
                      <Link to="mailto:hello@ironpeakmenshealth.com" className="text-primary hover:text-primary-hover transition-colors font-medium">
                        hello@ironpeakmenshealth.com
                      </Link>
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-border">
                <Link to="/ContactPage">
                  <Button size="lg" className="bg-accent text-accent-foreground hover:bg-accent-hover active:bg-accent-active shadow-medium hover:scale-105 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] focus-ring w-full sm:w-auto">
                    <Mail className="size-5" />
                    Contact Us About Privacy
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}